import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  MideyeTopBarAccountMenu,
  MideyeTopBarActionButton,
  MideyeTopBarShell,
} from "@mideye/top-bar";
import { API_PORTAL_APP_NAME, ApiPortalIcon, EamIcon } from "../appIdentity";
import { shellConfig } from "../lib/shellConfig";
import { useAuth } from "../lib/auth-context";
import type { UserInfo } from "../types/api";

const launcherEnvironment = detectEnvironment();
const showCrossAppNavigation = readCrossAppNavigationFeatureFlag();

// The same production build runs on both the dev and prod clusters, so
// import.meta.env.PROD can't tell them apart — detect from the runtime host so
// cross-app links resolve to the matching environment.
function detectEnvironment(): "dev" | "prod" {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "localhost" || host === "127.0.0.1" || host.includes(".dev.")) return "dev";
  return "prod";
}

function readCrossAppNavigationFeatureFlag(): boolean {
  // On by default; set VITE_SHOW_CROSS_APP_NAVIGATION=false to disable.
  return import.meta.env.VITE_SHOW_CROSS_APP_NAVIGATION !== "false";
}

// Get the Shield API docs URL based on current hostname
function getApiDocsUrl(): string {
  const hostname = window.location.hostname;
  if (hostname.includes("shield.") && hostname.endsWith(".mideye.com")) {
    return `${window.location.origin}/api/v2/redoc`;
  }
  // Local dev fallback
  return "https://shield.dev.mideye.com/api/v2/redoc";
}

export default function Layout() {
  const { isAuthenticated, isLoading, user, login, logout, error } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shell = shellConfig(pathname, user, API_PORTAL_APP_NAME);

  return (
    <MideyeTopBarShell
      appId={shell.appId}
      appName={shell.appName}
      appIcon={shell.icon === "eam" ? <EamIcon /> : <ApiPortalIcon />}
      extraApps={shell.extraApps}
      brandSubtitle={user?.company_id}
      brandHref="https://mideye.com"
      brandTarget="_blank"
      brandRel="noopener noreferrer"
      environment={launcherEnvironment}
      theme="light"
      userEmail={user?.email}
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
      signedOut={{
        title: "Sign in required",
        description: "Sign in to manage API keys for your company.",
        appName: API_PORTAL_APP_NAME,
        onSignIn: login,
        error,
      }}
      onNavigate={(href) => navigate(href)}
      navItems={[
        ...(shell.pageLink ? [shell.pageLink] : []),
        ...(shell.showPortalNav
          ? [
              { label: "API Docs", href: getApiDocsUrl(), external: true },
              {
                label: "About Shield",
                href: "https://mideye.com/shield/",
                external: true,
              },
            ]
          : []),
      ]}
      featureFlags={{ crossAppNavigation: showCrossAppNavigation }}
      showNavigationWhenSignedOut
      hideNavigationOnCompact={false}
      rightContent={getRightContent({
        isLoading,
        isAuthenticated,
        user,
        login,
        logout,
      })}
      contentClassName="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8"
    >
      <Outlet />
    </MideyeTopBarShell>
  );
}

function getRightContent({
  isLoading,
  isAuthenticated,
  user,
  login,
  logout,
}: {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: UserInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}) {
  if (isLoading) {
    return <div className="h-8 w-8 rounded-full bg-gray-100 animate-pulse" />;
  }

  if (isAuthenticated && user) {
    return <PortalUserActions user={user} onLogout={logout} />;
  }

  return (
    <MideyeTopBarActionButton variant="primary" onClick={login}>
      Sign in
    </MideyeTopBarActionButton>
  );
}

function PortalUserActions({
  user,
  onLogout,
}: {
  user: UserInfo;
  onLogout: () => Promise<void>;
}) {
  const roleLabel = user.can_manage_api_keys
    ? "API Key Manager"
    : user.can_manage_eam
      ? "Keycloak EAM Admin"
      : "Viewer";

  return (
    <MideyeTopBarAccountMenu
      avatarLabel={user.name || user.email}
      detail={roleLabel}
      displayName={user.name || user.email || "User"}
      items={[
        {
          label: "Sign out",
          onSelect: onLogout,
          testId: "sign-out-button",
          variant: "danger",
        },
      ]}
    />
  );
}
